import React, { useState, useEffect } from "react";
import Locations from "../../components/Locations";
import Timeline from "../../components/Timeline.js";
import Faqs from "../../components/Faqs";
import Pillars from "../../components/Pillars";
import SocialProof from "../../components/SocialProof";
import Countdown from "../../components/Countdown";
import RegistrationForm from "../../components/RegistrationForm";
import SEO from "../../components/SEO";
import eventSchema from "../../components/eventSchema";
import asset from "../../utils/assetUrl";

// Con la version ya puesta: la primera tiene que coincidir letra por letra con
// el <link rel="preload"> de public/index.html, o el navegador se baja hero.jpg
// dos veces.
const HERO_IMAGES = [
    asset('/assets/images/hero.jpg'),
    asset('/assets/images/hero2.jpg'),
    asset('/assets/images/hero3.jpg'),
    asset('/assets/images/hero4.jpg'),
];

function Home() {
    // El estado y la logica del formulario viven en RegistrationForm, que se
    // monta dos veces: en el hero y en el cierre.
    const scrollToHero = () => {
        const hero = document.getElementById('hero');
        if (!hero) {
            window.scrollTo({ top: 0, behavior: 'smooth' });
            return;
        }

        if (typeof hero.scrollIntoView === 'function') {
            hero.scrollIntoView({ behavior: 'smooth', block: 'start' });
            return;
        }

        const heroTop = hero.getBoundingClientRect().top + window.scrollY;
        window.scrollTo({ top: heroTop, behavior: 'smooth' });
    };

    const [currentImageIndex, setCurrentImageIndex] = useState(0);

    useEffect(() => {
        // La primera imagen la pinta el CSS; las otras tres suman ~2.6 MB y
        // antes se descargaban de inmediato, compitiendo con el LCP.
        // Ahora esperan a que la pagina termine de cargar.
        const preloadRest = () => {
            HERO_IMAGES.slice(1).forEach((src) => {
                const img = new Image();
                img.src = src;
            });
        };

        if (document.readyState === 'complete') {
            preloadRest();
            return undefined;
        }

        window.addEventListener('load', preloadRest, { once: true });
        return () => window.removeEventListener('load', preloadRest);
    }, []);

    useEffect(() => {
        const interval = setInterval(() => {
            setCurrentImageIndex((prevIndex) =>
                prevIndex === HERO_IMAGES.length - 1 ? 0 : prevIndex + 1
            );
        }, 4000);

        return () => clearInterval(interval);
    }, []);

    return (
        <>
            <SEO
                title="Conferencia para Canadá | Los lugares de Canadá donde sí puedes estudiar, trabajar y quedarte"
                description="Conferencia 100% gratuita en CDMX, Guadalajara y Monterrey. Habla cara a cara con instituciones educativas canadienses, un asesor laboral y un consultor migratorio regulado por el Gobierno de Canadá."
                keywords="Conferencia para canada, estudiar en canada, migrar a canada, trabajar en canada, provincias de canada, consultor migratorio regulado, residencia permanente"
                ogImage="https://conferenciaparacanada.com/assets/images/og.jpg"
                favicon="https://conferenciaparacanada.com/assets/images/favicon-32x32.png"
                appleFavicon="https://conferenciaparacanada.com/assets/images/apple-touch-icon.png"
                canonical="https://conferenciaparacanada.com/"
                jsonLd={eventSchema}
            />

            <div id="hero" className="hero nostatic">
                <div className="hero__bg" aria-hidden="true">
                    {HERO_IMAGES.map((src, idx) => (
                        <div
                            key={src}
                            className={`hero__bg-image ${idx === currentImageIndex ? 'is-active' : ''}`}
                            style={{ backgroundImage: `url(${src})` }}
                        />
                    ))}
                </div>
                <div className="container">
                    <div className="hero__inner">
                        <div className="info">

                            {/* El badge iba en contorno blanco sobre foto y se
                                perdia. Ahora va en amarillo solido: "gratis" es
                                el argumento que mas mueve el registro. */}
                            <span className="hero__badge">Entrada 100% gratis &middot; Cupos limitados</span>

                            {/* Un solo bloque para movil y escritorio. Antes
                                habia dos variantes ocultas por CSS, lo que
                                dejaba DOS <h1> en el DOM (Google lee ambos) y
                                tres copias del mismo kicker que se
                                desincronizaban al editar. */}
                            <h1>
                                Descubre los lugares de Canadá donde <span className="hl">sí puedes</span> estudiar, trabajar y quedarte
                            </h1>
                            {/* La enumeracion de los tres expositores vive en la
                                banda de pilares, que es donde se desarrolla;
                                repetirla aqui restaba aire al h1. El lead carga
                                el "gratis", que es lo que mueve el registro. */}
                            <p className="lead">
                                <strong className="hl-free">Entrada 100% gratuita.</strong> Un solo día para resolver
                                todas tus preguntas con expertos: estudios, empleo y migración.
                            </p>

                            {/* La bandera va como PNG local y no como emoji 🇲🇽:
                                Windows no trae fuente de banderas y Chrome
                                renderiza "MX" en su lugar. */}
                            <ul className="hero__dates list-unstyled">
                                {[
                                    { city: 'Ciudad de México', date: '6 de octubre' },
                                    { city: 'Guadalajara', date: '8 de octubre' },
                                    { city: 'Monterrey', date: '10 de octubre' }
                                ].map((sede) => (
                                    <li key={sede.city}>
                                        <img src={asset("/assets/images/flag-mx.png")} alt="" aria-hidden="true" />
                                        <span className="hero__date-text">
                                            <strong>{sede.city}</strong>
                                            <span>{sede.date}</span>
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        </div>

                        <RegistrationForm
                            instanceId="hero"
                            variant="hero"
                            title="Reserva tu lugar gratis"
                            subtitle="Cupo limitado en cada sede. Te enviamos por correo tu pase con código QR."
                        />
                    </div>
                </div>
            </div>

            {/* Quien expone va antes que el problema: el visitante primero
                quiere saber con quien va a hablar. */}
            <section id="pilares" className="pillars">
                <div className="container">
                    <div className="title">
                        <span className="pretitle dark">Tres razones para asistir</span>
                        <h2 className="gradient-title">Instituciones educativas, empleo y migración en el mismo día</h2>
                        <p className="lead">
                            Habla cara a cara con un consultor migratorio regulado por el Gobierno de Canadá, un
                            asesor laboral para darte estrategias de búsqueda de empleo e instituciones educativas
                            de reconocimiento en las mejores provincias para migrar. Todo en un solo lugar.
                        </p>
                    </div>
                    <Pillars />
                    <div className="pillars__actions">
                        <button className="button" onClick={scrollToHero}>Quiero hablar con ellos</button>
                    </div>
                </div>
            </section>

            <section
                id="problema"
                className="problem nostatic"
                style={{ backgroundImage: `url(${asset('/assets/images/toronto-skyline.jpg')})` }}
            >
                <div className="container">
                    <div className="problem__content">
                        <span className="pretitle">Lo primero que hay que entender</span>
                        <h2>Migrar a Canadá hoy no es igual que hace algunos años</h2>
                        <span className="problem__rule" aria-hidden="true" />
                        <p className="lead">
                            Cambiaron las reglas, cambiaron los requisitos y las provincias que antes eran la
                            entrada más fácil hoy ya no lo son. Buena parte de lo que circula en internet
                            quedó obsoleto.
                        </p>
                        <p>
                            Hoy hace falta una <strong>estrategia clara y al día</strong>. En la Conferencia vas a
                            entender cuál es el mejor camino para tu caso y cómo mejorar de verdad tus
                            probabilidades, y lo vas a escuchar de quienes están del otro lado del proceso.
                        </p>
                        <button className="button" onClick={scrollToHero}>Quiero saber mi mejor camino</button>
                    </div>
                </div>
            </section>

            <section id="locations" className="locations">
                <div className="container">
                    <div className="title">
                        <h2 className="gradient-title">Fechas y sedes en México</h2>
                        <p className="lead">
                            Tres ciudades en una misma semana. Elige la que te quede mejor y aparta tu lugar,
                            porque la entrada es gratuita pero el cupo de cada sede es limitado.
                        </p>
                    </div>
                    <Countdown />
                </div>
                <div className="container-max">
                    <div className="locations-container">
                        <Locations status="proximo" />
                    </div>
                    <div className="locations-actions">
                        <button className="button" onClick={scrollToHero}>Elegir mi ciudad</button>
                    </div>
                </div>
            </section>

            <section id="provincias" className="info">
                <div style={{ backgroundImage: `url(${asset('/assets/images/provincias.jpg')})` }} className="info__container nostatic">
                    <div className="container">
                        <div className="row row-va">
                            <div className="col-12 col-md-6">
                                <div className="left">
                                    <h2>LAS PROVINCIAS DEL FUTURO EN CANADÁ</h2>
                                    <p>
                                        Al pensar en Canadá, muchos imaginan Toronto, Vancouver o Montreal. Son las
                                        ciudades más conocidas, pero también las más saturadas y las más caras.
                                    </p>
                                    <p>
                                        En esta Conferencia vas a conocer <strong style={{ textDecoration: 'underline' }}>las
                                            provincias que hoy conviene mirar</strong>, donde vivir cuesta menos y el
                                        camino hacia la residencia permanente es más claro.
                                    </p>
                                </div>
                            </div>
                            <div className="col-12 col-md-6">
                                <div className="right">
                                    <p className="lead"><strong>Por qué estas provincias son las del futuro:</strong></p>

                                    <ul className="list-unstyled">
                                        <li><span>💰 </span><span><strong>Menor costo de vida:</strong> tu dinero rinde mucho más que en las grandes ciudades.</span></li>
                                        <li><span>💼 </span><span><strong>Mejores oportunidades laborales:</strong> sectores con demanda real y menos competencia.</span></li>
                                        <li><span>🏡 </span><span><strong>Más posibilidades de quedarte:</strong> programas provinciales que facilitan la permanencia a largo plazo.</span></li>
                                        <li><span>🌿 </span><span><strong>Mayor calidad de vida:</strong> ciudades en crecimiento, seguras y con espacio para tu familia.</span></li>
                                    </ul>
                                    <button className="button" onClick={scrollToHero}>Quiero ir a estas provincias</button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <section id="dinamica" className="expo">
                <div className="container">
                    <div className="title">
                        <span className="pretitle dark">Cómo funciona el evento</span>
                        <h2 className="gradient-title">Una conferencia y, después, tú frente a frente con los expertos</h2>
                    </div>

                    <div className="dynamic__grid">
                        <article className="dynamic__card">
                            <figure>
                                <img src={asset("/assets/images/eventos/conferencia-auditorio.jpg")} alt="Conferencia sobre costo de vida en Canadá ante un auditorio lleno" loading="lazy" />
                            </figure>
                            <div className="dynamic__body">
                                <span className="dynamic__step">Primero</span>
                                <h3>La conferencia</h3>
                                {/* Iba como un parrafo corrido con las tres
                                    preguntas encadenadas por comas y sin signos.
                                    En lista se leen de un vistazo. */}
                                <ul className="dynamic__list list-unstyled">
                                    <li>¿Qué está pidiendo Canadá hoy para estudiar, trabajar y migrar?</li>
                                    <li>¿Cuánto cuesta vivir en cada provincia?</li>
                                    <li>¿Qué caminos siguen abiertos?</li>
                                </ul>
                                <p className="dynamic__more">Y mucho más.</p>
                            </div>
                        </article>

                        <article className="dynamic__card">
                            <figure>
                                <img src={asset("/assets/images/eventos/asesoria-red-river.jpg")} alt="Atención uno a uno con el representante de una institución educativa canadiense" loading="lazy" />
                            </figure>
                            <div className="dynamic__body">
                                <span className="dynamic__step">Después</span>
                                <h3>Atención uno a uno</h3>
                                <p>
                                    Te acercas a cada expositor y le preguntas por tu caso, con tu
                                    perfil y tu presupuesto sobre la mesa. Nadie te va a dar una respuesta genérica.
                                </p>
                            </div>
                        </article>
                    </div>

                    <div className="dynamic__actions">
                        <button className="button" onClick={scrollToHero}>Aparta mi lugar gratis</button>
                    </div>
                </div>
            </section>

            <section id="testimonios" className="proof">
                <div className="container">
                    <div className="title">
                        <span className="pretitle dark">No lo decimos nosotros</span>
                        <h2 className="gradient-title">Así se vive la Conferencia para Canadá</h2>
                        <p className="lead">
                            Esto es lo que cuentan quienes ya asistieron, y así fue el ambiente en nuestras
                            ediciones anteriores.
                        </p>
                    </div>

                    {/* Aqui iban las cifras de la edicion de Colombia
                        (registrados, ciudades, instituciones). El cliente las
                        retiro; las fotos y los testimonios se quedan. */}
                    <SocialProof />

                    {/* El historico vive aqui, no en Fechas: es prueba social,
                        no logistica. Son las mismas ediciones de las fotos. */}
                    <div className="locations__past">
                        <p className="locations__past-title">Nuestro paso por Colombia, en febrero y marzo de 2026</p>
                        <div className="locations__past-grid">
                            <Locations status="realizado" />
                        </div>
                    </div>

                    <div className="proof__actions">
                        <button className="button" onClick={scrollToHero}>Quiero vivirlo también</button>
                    </div>
                </div>
            </section>

            <section id="timeline" className="timeline">
                <div className="container">
                    <div className="row">
                        <div className="col-12">
                            <div className="title">
                                <h2 className="gradient-title">Tu camino a Canadá, paso a paso</h2>
                                <p className="lead">Cuatro pasos desde que te registras hasta que empiezas tu proceso. Ninguno te cuesta nada.</p>
                            </div>
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-12">
                            <div className="timeline__content">
                                <Timeline />
                            </div>
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-12">
                            <div style={{ gap: '.5rem' }} className="timeline__actions d-flex flex-column align-items-center">
                                <button className="button" onClick={scrollToHero}>Dar el primer paso</button>
                                {/* Recoge el paso 1 del timeline y adelanta el 2. */}
                                <p className="small">Te toma menos de un minuto y recibes la confirmación en tu correo.</p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <section id="faqs" className="faqs">
                <div className="container containermin">
                    <div className="faqs__content">
                        <div className="title">
                            <span className="pretitle dark">¿Tienes dudas? Aquí las resolveremos</span>
                            <h2 className="gradient-title">Preguntas Frecuentes</h2>
                            <p>
                                Este es un evento exclusivo y gratuito donde tendrás contacto DIRECTO con
                                instituciones educativas canadienses, un asesor laboral y un consultor
                                migratorio regulado por el Gobierno de Canadá.
                            </p>
                        </div>
                        <Faqs />
                    </div>
                </div>
            </section >

            <section id="cta" className="cta">
                <div className="container">
                    {/* Segundo formulario. Antes este bloque tenia un boton que
                        devolvia al hero: seis secciones de scroll hacia arriba
                        para alguien que ya estaba convencido. */}
                    <div className="cta__grid">
                        <div className="cta__copy">
                            <h2 className="gradient-title">¡Reserva tu lugar ahora y da el primer paso hacia tu futuro en Canadá!</h2>
                            <p>
                                Vas a salir sabiendo qué programa te sirve, en qué provincia tiene sentido y cómo empezar el trámite. La entrada es gratuita y el cupo de cada sede es limitado.
                            </p>
                            <ul className="cta__checks list-unstyled">
                                <li><span aria-hidden="true">✅</span><span>Entrada 100% gratuita</span></li>
                                <li><span aria-hidden="true">✅</span><span>Instituciones educativas canadienses, asesor laboral y consultor migratorio regulado</span></li>
                                <li><span aria-hidden="true">✅</span><span>Atención uno a uno el mismo día</span></li>
                                <li><span aria-hidden="true">✅</span><span>Pase con código QR: se lo muestras a las instituciones y reciben tus datos</span></li>
                            </ul>
                        </div>

                        <RegistrationForm
                            instanceId="cierre"
                            variant="solid"
                            title="Reserva tu lugar gratis"
                            subtitle="Cupo limitado en cada sede. Te enviamos por correo tu pase con código QR."
                        />
                    </div>

                    <div className="row">
                        <div className="col-12">
                            <div className="title">
                                <ul className="list-unstyled social">
                                    <li>
                                        <a href="https://web.facebook.com/profile.php?id=100072255423424&_rdc=1&_rdr" target="_blank" rel="noreferrer" aria-label="Síguenos en Facebook">
                                            <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                                                <path d="M24 12.07C24 5.4 18.63 0 12 0S0 5.4 0 12.07C0 18.1 4.39 23.1 10.13 24v-8.44H7.08v-3.49h3.05V9.41c0-3.02 1.79-4.69 4.53-4.69 1.31 0 2.68.24 2.68.24v2.97h-1.51c-1.49 0-1.96.93-1.96 1.89v2.25h3.33l-.53 3.49h-2.8V24C19.61 23.1 24 18.1 24 12.07z" />
                                            </svg>
                                        </a>
                                    </li>
                                    <li>
                                        <a href="https://www.instagram.com/para.canada/" target="_blank" rel="noreferrer" aria-label="Síguenos en Instagram">
                                            <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                                                <path d="M12 2.16c3.2 0 3.58.01 4.85.07 1.17.05 1.8.25 2.23.41.56.22.96.48 1.38.9.42.42.68.82.9 1.38.16.42.36 1.06.41 2.23.06 1.27.07 1.65.07 4.85s-.01 3.58-.07 4.85c-.05 1.17-.25 1.8-.41 2.23-.22.56-.48.96-.9 1.38-.42.42-.82.68-1.38.9-.42.16-1.06.36-2.23.41-1.27.06-1.65.07-4.85.07s-3.58-.01-4.85-.07c-1.17-.05-1.8-.25-2.23-.41-.56-.22-.96-.48-1.38-.9-.42-.42-.68-.82-.9-1.38-.16-.42-.36-1.06-.41-2.23C2.17 15.58 2.16 15.2 2.16 12s.01-3.58.07-4.85c.05-1.17.25-1.8.41-2.23.22-.56.48-.96.9-1.38.42-.42.82-.68 1.38-.9.42-.16 1.06-.36 2.23-.41C8.42 2.17 8.8 2.16 12 2.16zM12 0C8.74 0 8.33.01 7.05.07 5.78.13 4.9.33 4.14.63c-.79.3-1.46.72-2.13 1.38C1.35 2.68.93 3.35.63 4.14.33 4.9.13 5.78.07 7.05.01 8.33 0 8.74 0 12s.01 3.67.07 4.95c.06 1.27.26 2.15.56 2.91.3.79.72 1.46 1.38 2.13.67.66 1.34 1.08 2.13 1.38.76.3 1.64.5 2.91.56C8.33 23.99 8.74 24 12 24s3.67-.01 4.95-.07c1.27-.06 2.15-.26 2.91-.56.79-.3 1.46-.72 2.13-1.38.66-.67 1.08-1.34 1.38-2.13.3-.76.5-1.64.56-2.91.06-1.28.07-1.69.07-4.95s-.01-3.67-.07-4.95c-.06-1.27-.26-2.15-.56-2.91-.3-.79-.72-1.46-1.38-2.13C21.32 1.35 20.65.93 19.86.63 19.1.33 18.22.13 16.95.07 15.67.01 15.26 0 12 0zm0 5.84a6.16 6.16 0 100 12.32 6.16 6.16 0 000-12.32zM12 16a4 4 0 110-8 4 4 0 010 8zm7.85-10.4a1.44 1.44 0 11-2.88 0 1.44 1.44 0 012.88 0z" />
                                            </svg>
                                        </a>
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <footer className="footer nostatic" style={{ backgroundImage: `url(${asset('/assets/images/footer.jpg')})` }}>
                <div className="container">
                    <div className="row footer-contact">
                        <div className="col-12">
                            <h2 className="gradient-title">¡Contáctanos!</h2>
                            <p>
                                Si tienes alguna pregunta, no dudes en contactarnos a <a style={{ display: 'block' }} className="block" target="_blank" rel="noreferrer" href="mailto:info@para-canada.com">info@para-canada.com</a>
                            </p>
                        </div>
                    </div>
                    <div className="row footer-copy">
                        <div className="col-12">
                            <p>
                                &copy; 2026. Todos los derechos reservados.
                                <span className="vpx">
                                    <a target="_blank" rel="noreferrer" href="https://vpixel.co?utm_source=confparacanada">Designed by VPixel CO</a>
                                </span>
                            </p>
                        </div>
                    </div>
                </div>
            </footer>

            <div className="sticky-cta">
                <button className="button" onClick={scrollToHero}>Regístrate gratis</button>
            </div>
        </>
    );
}

export default Home;
